/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { useUpdateEffect } from "react-use";
import { Tab, Tabs } from "@nordcloud/gnui";
import { ReservationsProvider } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { ROUTES } from "~/routing/routes";
import { ReservationBanner, ReservationPageLoader } from "../components";
import { CoverageChartWrapper } from "../CoverageChartWrapper";
import { getTooltipDetailsCallback } from "../getTooltipDetailsCallback";
import {
  useGetCurrentTab,
  useGetFilters,
  useGetTabsData,
  useGetUsageAnalysisData,
  useReservationFilters,
  useReservationState,
} from "../hooks";
import { RecommendationsSettingsWrapper } from "../RecommendationsSettingsWrapper";
import { ReservationsCoverageTable } from "../ReservationsCoverageTable";
import { TabSectionLabel } from "../TabSectionLabel";
import { CoverageChartFilters } from "../types";
import { getPaymentOptionsFilterprovider } from "../utils";
import { AzureUsageAnalysisGraph } from "./AzureUsageAnalysisGraph";
import { useAzureReservationsFeatureFlag } from "./hooks";

export function AzureCurrentBillingPeriodPage() {
  const {
    activeTab,
    offeringType,
    timePeriod,
    paymentOption,
    termOption,
    isSidebarOpen,
    coverageDetails,
    handleOpenSidebar,
    handleToggleSidebar,
    handleSetTimePeriod,
    handleOfferingType,
    handleUpdatePaymentOption,
    handleUpdateTermOption,
    setActiveTab,
  } = useReservationState(ReservationsProvider.Azure);

  // so far we are not using any filters for Azure
  const filters: CoverageChartFilters = useGetFilters();
  const { serviceUsageType, reservationOfferingType, tabsSelection, loading } =
    useAzureReservationsFeatureFlag(activeTab);

  const { isComputeTab } = useGetCurrentTab(serviceUsageType);

  const { currentOfferingType, selectedFilters } = useGetTabsData({
    isComputeTab,
    showFilters: true,
    offeringType,
    reservationOfferingType,
    filters,
  });

  const { activeFilters, removeFields, clearFilters, removeFilter } =
    useReservationFilters(filters);

  useUpdateEffect(clearFilters, [activeTab]);

  const {
    loading: isGraphLoading,
    utilisationChartError,
    timeSeries,
    highestCost,
    reservedInstaceCoverage,
    totalOnDemandCost,
    savedCost,
  } = useGetUsageAnalysisData({
    serviceUsageType,
    timePeriod,
    filters,
    provider: ReservationsProvider.Azure,
  });

  const tooltipDetailCallback = getTooltipDetailsCallback(
    ReservationsProvider.Azure
  );

  const paymentOptionsFilter = getPaymentOptionsFilterprovider(
    ReservationsProvider.Azure
  );

  return (
    <LoaderWrap loading={loading} Component={ReservationPageLoader}>
      <Tabs step={activeTab} handleTab={setActiveTab}>
        {tabsSelection.map(
          ({ label, icon, serviceType, isActiveTab, bannerText, idText }) => (
            <Tab
              key={label}
              width="auto"
              label={<TabSectionLabel icon={icon} label={label} />}
            >
              <If condition={isActiveTab}>
                <Then>
                  <CoverageChartWrapper
                    showFiltersOption
                    anyTab
                    idText={idText}
                    serviceType={serviceType}
                    filters={filters}
                    timePeriod={timePeriod}
                    activeFilters={activeFilters}
                    provider={ReservationsProvider.Azure}
                    offeringType={currentOfferingType}
                    onSetTimePeriodClick={handleSetTimePeriod}
                    onRemoveFields={removeFields}
                    onClearFilters={clearFilters}
                    onRemoveFilter={removeFilter}
                  >
                    <AzureUsageAnalysisGraph
                      serviceUsageType={serviceType}
                      graphLoading={isGraphLoading}
                      timeSeries={timeSeries}
                      highestCost={highestCost}
                      utilisationChartError={utilisationChartError}
                      reservedInstaceCoverage={reservedInstaceCoverage}
                      savedCost={savedCost}
                      totalOnDemandCost={totalOnDemandCost}
                      tooltipDetailCallback={tooltipDetailCallback}
                    />
                  </CoverageChartWrapper>
                  <RecommendationsSettingsWrapper
                    idText={idText}
                    paymentOption={paymentOption}
                    termOption={termOption}
                    serviceType={serviceType}
                    timePeriod={timePeriod}
                    currentOfferingType={currentOfferingType}
                    selectedFilters={selectedFilters}
                    paymentOptionsFilter={paymentOptionsFilter}
                    provider={ReservationsProvider.Azure}
                    onOfferingType={handleOfferingType}
                    onUpdatePaymentOption={handleUpdatePaymentOption}
                    onUpdateTermOption={handleUpdateTermOption}
                  />

                  <ReservationsCoverageTable
                    idText={idText}
                    termOption={termOption}
                    paymentOption={paymentOption}
                    coverageDetails={coverageDetails}
                    serviceType={serviceType}
                    selectedFilters={selectedFilters}
                    timePeriod={timePeriod}
                    currentOfferingType={currentOfferingType}
                    isSidebarOpen={isSidebarOpen}
                    provider={ReservationsProvider.Azure}
                    paymentOptionsFilter={paymentOptionsFilter}
                    hasGroupByParameter={!isComputeTab}
                    onOpenSidebar={handleOpenSidebar}
                    onToggleSidebar={handleToggleSidebar}
                  />
                </Then>
                <Else>
                  <ReservationBanner
                    isLink
                    heading="No Data Available"
                    subText={bannerText}
                    buttonText="Add Billing Data"
                    routePath={ROUTES.cloudAccounts.index}
                  />
                </Else>
              </If>
            </Tab>
          )
        )}
      </Tabs>
    </LoaderWrap>
  );
}
